import { makeAutoObservable } from "mobx";
import { EntityRepository } from "@/app_store/stream/entity_repository";
import { StreamEndpoint } from "@/app_store/stream/stream_endpoint";
import { AgentStore } from "@/app_store/agent_store";
import { router as handlersRouter } from "@/api/handlers";
import { router as producersRouter } from "@/api/producers";
import { AccountStore } from "@/app_store/account";
import { ProjectStore } from "@/app_store/project";
import { SettingsStore } from "@/app_store/settings";
import { GitStore } from "@/app_store/git";
import { DeploymentServiceStore } from "@/app_store/deployment_service";
import { DeploymentStore } from "@/app_store/deployment_store";
import { BrowserStore } from "@/app_store/browser";
import { TerminalStore } from "@/app_store/terminal";
import { StateStore } from "@/app_store/state";

interface Greeting {
    id: string;
    message: string;
}

class GreetingStore {
    repository = new EntityRepository<Greeting>("id");

    constructor() {
        makeAutoObservable(this, { repository: false });
    }

    get message(): string | null {
        return this.repository.active[0]?.value.message ?? null;
    }
}

class AppStore {
    account: AccountStore;
    greeting: GreetingStore;
    project: ProjectStore;
    settings: SettingsStore;
    git: GitStore;
    deploymentService: DeploymentServiceStore;
    deploy: DeploymentStore;
    browser: BrowserStore;
    terminal: TerminalStore;
    state: StateStore;
    apiUrl: string = "";
    proxyBaseUrl: string = "";
    streamEndpoint: StreamEndpoint;
    agent: AgentStore;
    isRefreshing: boolean = false;

    constructor() {
        makeAutoObservable(this, { streamEndpoint: false, agent: false });
        this.apiUrl = (window as any).config?.api_url || document.location.origin;
        this.proxyBaseUrl = (window as any).config?.proxy_url || document.location.origin;
        this.account = new AccountStore(this);
        this.greeting = new GreetingStore();
        this.project = new ProjectStore(this);
        this.settings = new SettingsStore(this);
        this.state = new StateStore();
        this.deploymentService = new DeploymentServiceStore();
        this.streamEndpoint = new StreamEndpoint({
            greeting: this.greeting.repository,
            account: this.account.repository,
            project: this.project.repository,
            deployment_service: this.deploymentService.repository,
        });
        this.agent = new AgentStore(handlersRouter, producersRouter, this);
        this.git = new GitStore(this);
        this.deploy = new DeploymentStore(this);
        this.browser = new BrowserStore(this);
        this.terminal = new TerminalStore(this);
    }

    get isInitialized() {
        return this.streamEndpoint.isInitialized;
    }

    startRefresh() {
        this.isRefreshing = true;
        location.reload();
    }

    get isSyncing() {
        return this.streamEndpoint.isSyncing;
    }

    openStream() {
        this.streamEndpoint.updateConsumerOpts({
            consumerType: "SSE",
            url: `${this.apiUrl}/api/stream/`,
            authToken: this.account.token ?? undefined
        });
        this.streamEndpoint.start();
        this.agent.connect(this.account.token!, this.apiUrl);
    }

    closeStream() {
        this.streamEndpoint.stop();
        this.agent.disconnect();
    }
}

export const appStore = new AppStore();

// Debug console access: run `dump(appStore)` (or `dump(appStore.agent.session)`, any
// observable subtree) in devtools for a plain, expandable snapshot, or `dump(x, true)`
// for a JSON string. Skips underscore-prefixed fields (this codebase's convention for
// internal plumbing, e.g. every store's `_root` back-reference to the whole AppStore --
// mobx's own toJS() doesn't filter these out, so dumping through a `_root` cycles right
// back to the object you started from and JSON.stringify throws on the result).
function dump(value: any, asJson?: boolean): any {
    const seen = new Map<any, any>();
    function walk(v: any): any {
        if (v === null || typeof v !== "object" || v instanceof Date) return v;
        if (seen.has(v)) return seen.get(v);
        if (Array.isArray(v)) {
            const out: any[] = [];
            seen.set(v, out);
            v.forEach(item => out.push(walk(item)));
            return out;
        }
        if (v instanceof Map) {
            const out: Record<string, any> = {};
            seen.set(v, out);
            v.forEach((val, key) => { out[String(key)] = walk(val); });
            return out;
        }
        if (v instanceof Set) {
            const out: any[] = [];
            seen.set(v, out);
            v.forEach(item => out.push(walk(item)));
            return out;
        }
        const out: Record<string, any> = {};
        seen.set(v, out);
        for (const key of Object.keys(v)) {
            if (key.startsWith("_")) continue;
            const val = (v as any)[key];
            if (typeof val === "function") continue;
            out[key] = walk(val);
        }
        return out;
    }
    const plain = walk(value);
    return asJson ? JSON.stringify(plain, null, 2) : plain;
}

(window as any).appStore = appStore;
(window as any).dump = dump;

// rpcSession() — call in devtools to print the full agent RPC session state.
// Prints three sections: transport (connection + in-flight), session (the live
// SessionStore snapshot), and rpcQueue (calls awaiting acknowledgement).
(window as any).rpcSession = function rpcSession() {
    const agent = appStore.agent as any;
    const transport = {
        isConnected: agent.isConnected,
        isOperationPending: agent.isOperationPending,
        pendingRequests: (() => {
            const out: Record<string, any> = {};
            (agent._pendingRequests as Map<string, any>).forEach((entry, id) => {
                out[id] = { mode: entry.mode, buffered: entry.itemBuffer?.length ?? 0 };
            });
            return out;
        })(),
        chunkBuffer: (() => {
            const out: Record<string, number> = {};
            (agent._chunkBuffer as Map<string, string>).forEach((v, k) => { out[k] = v.length; });
            return out;
        })(),
        rpcQueue: (agent._rpcQueue as any[]).map(e => ({ request_id: e.request_id, type: (e.data as any)?.type })),
    };
    console.group("%c[rpcSession] transport", "font-weight:bold;color:#4af");
    console.log(transport);
    console.groupEnd();
    console.group("%c[rpcSession] session", "font-weight:bold;color:#4af");
    console.log(dump(agent.session));
    console.groupEnd();
};
